// Unified, event-driven readiness gate for smooth first animations
// - No arbitrary timeouts
// - Waits for fonts, three.js loading manager, image decode, optional video readiness
// - Flushes with two rAFs to ensure first paint has happened

let threePending = 0;
const threeWaiters = new Set();
let threeManager = null;
let threeManagerWired = false;
let netHooksAttached = false;
let inflight = 0;
const netWaiters = new Set();
let netQuietTimer = null;
const inflightListeners = new Set();
const defaultIgnorePatterns = [
  /\.m3u8(\?.*)?$/i,
  /\.mp4(\?.*)?$/i,
  /\.webm(\?.*)?$/i,
  /\.m4s(\?.*)?$/i, // DASH/HLS segments
  /\.mpd(\?.*)?$/i, // DASH manifests
  /\/api\/video-proxy(\?.*)?$/i,
  /\.ts(\?.*)?$/i,
  /\/video\//i,
  /_next\/static\//i,
  /analytics/i,
  /stream\.mux\.com/i, // Mux HLS domain (manifests, segments, keys)
  /\.r2\.dev\//i,
];
let shouldIgnoreUrl = (url) => defaultIgnorePatterns.some((re) => re.test(url));

function flushThreeWaiters() {
  if (threePending === 0) {
    threeWaiters.forEach((resolve) => resolve());
    threeWaiters.clear();
  }
}

export function setThreeLoadingManager(manager) {
  if (!manager || threeManagerWired) return;
  threeManager = manager;
  const prevOnStart = manager.onStart;
  const prevOnLoad = manager.onLoad;
  const prevOnError = manager.onError;
  const prevOnProgress = manager.onProgress;

  manager.onStart = function onStart(url, itemsLoaded, itemsTotal) {
    threePending = Math.max(0, (itemsTotal || 0) - (itemsLoaded || 0));
    if (typeof prevOnStart === "function")
      prevOnStart(url, itemsLoaded, itemsTotal);
  };

  manager.onProgress = function onProgress(url, itemsLoaded, itemsTotal) {
    threePending = Math.max(0, (itemsTotal || 0) - (itemsLoaded || 0));
    if (typeof prevOnProgress === "function")
      prevOnProgress(url, itemsLoaded, itemsTotal);
    if (threePending === 0) flushThreeWaiters();
  };

  manager.onLoad = function onLoad() {
    threePending = 0;
    if (typeof prevOnLoad === "function") prevOnLoad();
    flushThreeWaiters();
  };

  manager.onError = function onError(url) {
    threePending = Math.max(0, threePending - 1);
    if (typeof prevOnError === "function") prevOnError(url);
    if (threePending === 0) flushThreeWaiters();
  };

  threeManagerWired = true;
}

function attachNetworkHooks() {
  if (netHooksAttached || typeof window === "undefined") return;
  netHooksAttached = true;

  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = async (input, init) => {
      try {
        const url = typeof input === "string" ? input : input?.url || "";
        const track = url && !shouldIgnoreUrl(url);
        if (track) {
          inflight++;
          notifyInflightChange();
        }
        const p = originalFetch(input, init);
        p.finally(() => {
          if (track) {
            inflight = Math.max(0, inflight - 1);
            notifyInflightChange();
            if (inflight === 0) notifyNetWaiters();
          }
        });
        return p;
      } catch (e) {
        return originalFetch(input, init);
      }
    };
  }
  // Patch XHR prototype methods instead of replacing constructor
  const proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (proto && !proto.__ninePatched) {
    const origOpen = proto.open;
    const origSend = proto.send;
    proto.open = function (method, url, ...rest) {
      try {
        this.__nineTracked = url && !shouldIgnoreUrl(String(url));
      } catch (_) {
        this.__nineTracked = false;
      }
      return origOpen.call(this, method, url, ...rest);
    };
    proto.send = function (...args) {
      let incremented = false;
      try {
        if (this.__nineTracked) {
          inflight++;
          incremented = true;
          notifyInflightChange();
        }
      } catch (_) {
        // ignore
      }
      try {
        // Ensure we always decrement even if the XHR errors or 404s
        // 'loadend' fires for both success and failure.
        if (typeof this.addEventListener === "function") {
          this.addEventListener(
            "loadend",
            () => {
              if (incremented) {
                inflight = Math.max(0, inflight - 1);
                notifyInflightChange();
                if (inflight === 0) notifyNetWaiters();
              }
            },
            { once: true }
          );
        } else if (incremented) {
          // Extremely defensive fallback; schedule a microtask decrement
          setTimeout(() => {
            inflight = Math.max(0, inflight - 1);
            notifyInflightChange();
            if (inflight === 0) notifyNetWaiters();
          }, 0);
        }
      } catch (_) {
        // If we fail to attach the listener, don't get stuck in a busy state
        if (incremented) {
          setTimeout(() => {
            inflight = Math.max(0, inflight - 1);
            notifyInflightChange();
            if (inflight === 0) notifyNetWaiters();
          }, 0);
        }
      }
      return origSend.apply(this, args);
    };
    Object.defineProperty(proto, "__ninePatched", { value: true });
  }
}

function notifyNetWaiters() {
  netWaiters.forEach((resolve) => resolve());
  netWaiters.clear();
}

function notifyInflightChange() {
  inflightListeners.forEach((fn) => {
    try {
      fn(inflight);
    } catch (_) {}
  });
}

function onInflightChange(fn) {
  inflightListeners.add(fn);
  return () => inflightListeners.delete(fn);
}

function waitForNetworkIdle({ quietMs = 300, maxWaitMs = 4000 } = {}) {
  if (typeof window === "undefined") return Promise.resolve();
  attachNetworkHooks();
  // Resolve after network returns to baseline and stays quiet
  const baseline = inflight;
  return new Promise((resolve) => {
    let quietTimer = null;
    let hardTimeout = null;

    function done() {
      cleanup();
      resolve();
    }

    function cleanup() {
      if (unsubscribe) unsubscribe();
      if (quietTimer) clearTimeout(quietTimer);
      if (hardTimeout) clearTimeout(hardTimeout);
    }

    function scheduleQuiet() {
      if (quietTimer) clearTimeout(quietTimer);
      quietTimer = setTimeout(done, quietMs);
    }

    function handleChange() {
      if (inflight <= baseline) {
        scheduleQuiet();
      } else {
        if (quietTimer) {
          clearTimeout(quietTimer);
          quietTimer = null;
        }
      }
    }

    const unsubscribe = onInflightChange(handleChange);
    hardTimeout = setTimeout(done, maxWaitMs);
    // Initial check
    handleChange();
  });
}

function waitForThreeIdle(maxWaitMs = 10000) {
  if (typeof window === "undefined") return Promise.resolve();
  if (!threeManager || threePending === 0) return Promise.resolve();
  return new Promise((resolve) => {
    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        console.warn(
          `[pageReady] waitForThreeIdle timed out after ${maxWaitMs}ms (${threePending} items pending)`
        );
        resolve();
      }
    }, maxWaitMs);

    threeWaiters.add(() => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        resolve();
      }
    });
  });
}

function waitForImage(img, maxWaitMs = 8000) {
  if (!img) return Promise.resolve();
  // Already decoded and available
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();

  return new Promise((resolve) => {
    let resolved = false;
    let timeout = null;

    const done = () => {
      if (resolved) return;
      resolved = true;
      if (timeout) clearTimeout(timeout);
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      resolve();
    };

    timeout = setTimeout(() => {
      if (!resolved) {
        console.warn(
          `[pageReady] waitForImage timed out after ${maxWaitMs}ms for`,
          img.src || img
        );
        done();
      }
    }, maxWaitMs);

    if (typeof img.decode === "function") {
      img.decode().then(done).catch(done);
    }

    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  });
}

function waitForImages(root) {
  if (typeof window === "undefined") return Promise.resolve();
  const scope = root && root.querySelectorAll ? root : document;
  const imgs = Array.from(scope.querySelectorAll("img"));
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(imgs.map((i) => waitForImage(i)));
}

function waitForVideo(video, maxWaitMs = 8000) {
  if (!video) return Promise.resolve();
  if (video.readyState >= 2) return Promise.resolve();
  if (video.preload === "none") return Promise.resolve();
  if (!video.src && !video.currentSrc) return Promise.resolve();
  return new Promise((resolve) => {
    let resolved = false;
    let timeout = null;

    const done = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    };

    function cleanup() {
      if (timeout) clearTimeout(timeout);
      video.removeEventListener("loadeddata", done);
      video.removeEventListener("canplaythrough", done);
      video.removeEventListener("error", done);
    }

    timeout = setTimeout(() => {
      if (!resolved) {
        console.warn(
          `[pageReady] waitForVideo timed out after ${maxWaitMs}ms for`,
          video.src || video
        );
        done();
      }
    }, maxWaitMs);

    video.addEventListener("loadeddata", done, { once: true });
    video.addEventListener("canplaythrough", done, { once: true });
    video.addEventListener("error", done, { once: true });
  });
}

function waitForVideos(root) {
  if (typeof window === "undefined") return Promise.resolve();
  const scope = root && root.querySelectorAll ? root : document;
  const videos = Array.from(scope.querySelectorAll("video"));
  if (videos.length === 0) return Promise.resolve();
  return Promise.all(videos.map((v) => waitForVideo(v)));
}

function nextFrames(count = 2) {
  if (typeof window === "undefined") return Promise.resolve();
  return new Promise((resolve) => {
    let remaining = Math.max(0, count);
    function step() {
      if (remaining <= 0) return resolve();
      remaining -= 1;
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  });
}

export async function awaitPageReady({
  root = typeof document !== "undefined" ? document : null,
  includeVideos = true,
  includeNetworkIdle = true,
} = {}) {
  if (typeof window === "undefined") return; // SSR no-op

  const fontsReady =
    typeof document !== "undefined" &&
    document.fonts &&
    typeof document.fonts.ready?.then === "function"
      ? document.fonts.ready.catch(() => {})
      : Promise.resolve();

  const threeIdle = waitForThreeIdle();
  const images = waitForImages(root);
  const videos = includeVideos ? waitForVideos(root) : Promise.resolve();
  const networkIdle = includeNetworkIdle
    ? waitForNetworkIdle({ quietMs: 300 })
    : Promise.resolve();

  await Promise.all([fontsReady, threeIdle, images, videos, networkIdle]);
  await nextFrames(2);
}

// Export individual waiters for use with unified loading manager
export {
  waitForThreeIdle,
  waitForImages,
  waitForVideos,
  waitForNetworkIdle,
  nextFrames,
};

// Export Three.js pending count for progress tracking
export function getThreePending() {
  return threePending;
}

export function isThreeManagerWired() {
  return threeManagerWired;
}

export default awaitPageReady;
